import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InventoryEquipment.css';

const CREATE = `
  mutation CreateVehicle($createVehicleInput: CreateVehicleInput!) {
    createVehicle(createVehicleInput: $createVehicleInput) { id vehicleNumber }
  }
`;

const UPDATE = `
  mutation UpdateVehicle($updateVehicleInput: UpdateVehicleInput!) {
    updateVehicle(updateVehicleInput: $updateVehicleInput) { id vehicleNumber }
  }
`;

function toDateInput(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function splitMakeModel(row) {
  if (row?.make || row?.model) {
    return { make: row.make || '', model: row.model || '' };
  }
  const s = String(row?.makeModel || '').trim();
  if (!s) return { make: '', model: '' };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { make: parts[0], model: '' };
  return { make: parts[0], model: parts.slice(1).join(' ') };
}

function emptyForm() {
  return {
    make: '',
    model: '',
    registration: '',
    assignedDriver: '',
    currentSite: '',
    fuelThisMonth: '',
    serviceDueDate: '',
    registrationExpiry: '',
    notes: '',
  };
}

export default function VehicleForm({
  onClose,
  onSaved,
  editData = null,
  nextVehicleNumber = 'VEH-001',
}) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!editData) {
      setForm(emptyForm());
      return;
    }
    const names = splitMakeModel(editData);
    setForm({
      make: names.make,
      model: names.model,
      registration: editData.registration || '',
      assignedDriver: editData.assignedDriver || '',
      currentSite: editData.currentSite || '',
      fuelThisMonth:
        editData.fuelThisMonth != null ? String(editData.fuelThisMonth) : '',
      serviceDueDate: toDateInput(editData.serviceDueDate),
      registrationExpiry: toDateInput(editData.registrationExpiry),
      notes: editData.notes || '',
    });
  }, [editData]);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.make.trim() && !form.model.trim()) {
      toast.error('Make or model is required');
      return;
    }
    const payload = {
      make: form.make.trim() || undefined,
      model: form.model.trim() || undefined,
      registration: form.registration.trim() || undefined,
      assignedDriver: form.assignedDriver.trim() || undefined,
      currentSite: form.currentSite.trim() || undefined,
      fuelThisMonth:
        form.fuelThisMonth === ''
          ? undefined
          : Number.parseFloat(form.fuelThisMonth),
      serviceDueDate: form.serviceDueDate || undefined,
      registrationExpiry: form.registrationExpiry || undefined,
      notes: form.notes.trim() || undefined,
    };

    setLoading(true);
    try {
      if (editData?.id) {
        await graphqlRequest(UPDATE, {
          updateVehicleInput: { id: editData.id, ...payload },
        });
        toast.success('Vehicle updated');
      } else {
        await graphqlRequest(CREATE, { createVehicleInput: payload });
        toast.success('Vehicle added');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  const vehicleNumber = editData?.vehicleNumber || nextVehicleNumber;

  return (
    <div className="ieq-overlay" role="dialog" aria-modal="true">
      <div className="ieq-modal ieq-modal--form">
        <header className="ieq-modal-header">
          <h2>
            {editData
              ? `Edit Vehicle — ${editData.vehicleNumber || ''}`
              : 'Add Vehicle'}
          </h2>
          <button
            type="button"
            className="ieq-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>
        <form className="ieq-modal-body" onSubmit={handleSubmit}>
          <div className="ieq-grid">
            <label className="ieq-field">
              <span>Vehicle ID</span>
              <input value={vehicleNumber} disabled />
            </label>
            <label className="ieq-field">
              <span>Registration</span>
              <input
                value={form.registration}
                onChange={set('registration')}
                placeholder="e.g. 1SAS001"
              />
            </label>
            <label className="ieq-field">
              <span>
                Make <em>*</em>
              </span>
              <input
                value={form.make}
                onChange={set('make')}
                placeholder="e.g. Toyota"
                required
              />
            </label>
            <label className="ieq-field">
              <span>Model</span>
              <input
                value={form.model}
                onChange={set('model')}
                placeholder="e.g. HiAce"
              />
            </label>
            <label className="ieq-field">
              <span>Assigned Driver</span>
              <input
                value={form.assignedDriver}
                onChange={set('assignedDriver')}
                placeholder="Staff name"
              />
            </label>
            <label className="ieq-field">
              <span>Current Site</span>
              <input
                value={form.currentSite}
                onChange={set('currentSite')}
                placeholder="Site / warehouse"
              />
            </label>
            <label className="ieq-field">
              <span>Fuel This Month</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.fuelThisMonth}
                onChange={set('fuelThisMonth')}
                placeholder="0.00"
              />
            </label>
            <label className="ieq-field">
              <span>Service Due</span>
              <input
                type="date"
                value={form.serviceDueDate}
                onChange={set('serviceDueDate')}
              />
            </label>
            <label className="ieq-field">
              <span>Registration Expiry</span>
              <input
                type="date"
                value={form.registrationExpiry}
                onChange={set('registrationExpiry')}
              />
            </label>
            <label className="ieq-field ieq-field--full">
              <span>Notes</span>
              <textarea rows={3} value={form.notes} onChange={set('notes')} />
            </label>
          </div>
          <div className="ieq-modal-footer">
            <button type="button" className="ieq-btn ieq-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="ieq-btn ieq-btn--primary"
              disabled={loading}
            >
              {loading ? 'Saving…' : editData ? 'Save Changes' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
